import React, { useRef, useState } from "react";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  ArrowRight,
  Building2,
  CheckCircle2,
  Factory,
  FileCheck2,
  Globe2,
  Loader2,
  Mail,
  PackageSearch,
  Pill,
  Plane,
  RadioTower,
  Search,
  ShieldCheck,
  Ship,
  Truck,
  Upload,
  X,
} from "lucide-react";
import { toast } from "sonner";
import { useLanguage } from "../context/LanguageContext";
import { submitRFQ, uploadFile } from "../lib/api";

const SERVICE_CARDS = [
  {
    id: "telecom",
    icon: RadioTower,
    title: { ar: "مواد ومعدات الاتصالات", en: "Telecom Materials & Equipment" },
    description: {
      ar: "البحث والتوريد والاستيراد لمواد ومعدات الاتصالات والشبكات والطاقة والبنية التحتية من الموردين الأوروبيين.",
      en: "European sourcing for telecom, network, power and infrastructure materials from qualified suppliers.",
    },
    items: {
      ar: [
        "أبراج الاتصالات والأعمدة وملحقاتها",
        "الهوائيات وملحقات RF والكوابل والموصلات",
        "الألياف البصرية وFTTH / FTTx وOSP",
        "السويتشات والراوترات ومعدات الشبكات",
        "الكبائن والراكات والشيلترات ومراكز البيانات",
        "الـ Rectifiers والبطاريات وUPS وأنظمة الطاقة",
        "التأريض والحماية من الصواعق وإضاءة الطيران",
        "معدات المايكروويف والربط الخلفي",
        "ELV وأنظمة الأمن والمراقبة",
        "أدوات القياس والاختبار وقطع الغيار المتخصصة",
      ],
      en: [
        "Telecom towers, poles and accessories",
        "Antennas, RF accessories, feeders and connectors",
        "Fiber optic, FTTH / FTTx and OSP materials",
        "Switches, routers and network equipment",
        "Cabinets, racks, shelters and data-center equipment",
        "Rectifiers, batteries, UPS and power systems",
        "Earthing, lightning protection and aviation lights",
        "Microwave and backhaul equipment",
        "ELV, security and surveillance systems",
        "Test equipment, tools and specialized spare parts",
      ],
    },
  },
  {
    id: "supplements",
    icon: Pill,
    title: { ar: "المكملات الغذائية والفيتامينات", en: "Supplements & Vitamins" },
    description: {
      ar: "خدمات بحث وتوريد للمكملات والمنتجات الصحية من مصنعين وموردين أوروبيين، بما في ذلك خيارات العلامة الخاصة والتصنيع التعاقدي.",
      en: "Sourcing of supplements and wellness products from European manufacturers, including private-label and contract-manufacturing options.",
    },
    items: {
      ar: [
        "الفيتامينات والمعادن والمالتي فيتامين",
        "Omega-3 والكولاجين والبروبيوتيك",
        "التغذية الرياضية والبروتين والأحماض الأمينية",
        "منتجات الصحة والعافية",
        "Private Label والمنتجات بعلامة العميل",
        "Contract Manufacturing والتصنيع التعاقدي",
        "توريد بالجملة للموزعين والشركات",
      ],
      en: [
        "Vitamins, minerals and multivitamins",
        "Omega-3, collagen and probiotics",
        "Sports nutrition, protein and amino acids",
        "Health and wellness products",
        "Private-label products",
        "Contract manufacturing",
        "Bulk supply for distributors and companies",
      ],
    },
    regulated: true,
  },
  {
    id: "corporate",
    icon: Building2,
    title: { ar: "توريد الشركات والمشاريع", en: "Corporate & Project Supply" },
    description: {
      ar: "دعم أقسام المشتريات والمقاولين والمشاريع في السعودية والخليج للوصول إلى المصنعين والموردين الأوروبيين المناسبين.",
      en: "Procurement support for companies, contractors and projects in Saudi Arabia and the GCC seeking European manufacturers and suppliers.",
    },
    items: {
      ar: [
        "البحث عن المصنع أو المورد المناسب",
        "مقارنة العروض الفنية والتجارية",
        "مراجعة المواصفات وطلبات الـ BOQ",
        "التفاوض التجاري وتنسيق الطلبات",
        "تجميع الشحنات من أكثر من مورد",
        "مستندات التصدير والشحن الدولي",
        "الشحن الجوي والبحري والبري",
        "المتابعة حتى التسليم إلى العميل أو المشروع",
      ],
      en: [
        "Supplier and manufacturer sourcing",
        "Technical and commercial quote comparison",
        "Specification and BOQ review",
        "Commercial negotiation and order coordination",
        "Consolidation from multiple suppliers",
        "Export documentation and international logistics",
        "Air, sea and road freight coordination",
        "Follow-up through delivery to the customer or project",
      ],
    },
  },
];

const PROCESS = [
  { icon: Search, ar: "نبحث", en: "Source", arText: "نبحث عن الموردين والمصنعين المناسبين في أوروبا.", enText: "We identify suitable European suppliers and manufacturers." },
  { icon: FileCheck2, ar: "نقارن", en: "Compare", arText: "نقارن العروض والمواصفات والشروط التجارية.", enText: "We compare quotations, specifications and commercial terms." },
  { icon: Factory, ar: "ننسق", en: "Coordinate", arText: "ننسق الطلب والمستندات والتجهيز مع المورد.", enText: "We coordinate orders, documents and supplier preparation." },
  { icon: Ship, ar: "نشحن", en: "Deliver", arText: "ننسق الشحن والمتابعة حتى الوصول للوجهة المطلوبة.", enText: "We coordinate logistics and follow the shipment through delivery." },
];

const INITIAL_FORM = {
  category: "telecom",
  product_description: "",
  quantity: "",
  preferred_origin: "Europe",
  delivery_time: "",
  company_name: "",
  full_name: "",
  position: "",
  email: "",
  mobile: "",
  whatsapp: "",
  country: "Saudi Arabia",
  city: "",
  project_name: "",
  notes: "",
  files: [],
};

const Field = ({ label, required, children }) => (
  <label className="block">
    <span className="block mb-2 text-sm font-medium text-slate-300">
      {label}{required && <span className="text-[#D4AF37] ms-1">*</span>}
    </span>
    {children}
  </label>
);

const inputClass = "w-full bg-[#0A1223] border border-white/12 text-white px-4 py-3.5 outline-none focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] transition-colors placeholder:text-slate-600";

export default function ImportFromEurope() {
  const { lang, isRTL } = useLanguage();
  const isAr = lang === "ar";
  const fileRef = useRef(null);
  const [form, setForm] = useState(INITIAL_FORM);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);

  const tx = (ar, en) => (isAr ? ar : en);
  const Arrow = isRTL ? ArrowLeft : ArrowRight;
  const selectedService = SERVICE_CARDS.find((x) => x.id === form.category) || SERVICE_CARDS[0];

  const set = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const handleFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        const uploaded = await uploadFile(file);
        setForm((prev) => ({
          ...prev,
          files: [...prev.files, {
            id: uploaded.id,
            original_filename: uploaded.original_filename,
            storage_path: uploaded.storage_path,
            content_type: uploaded.content_type,
            size: uploaded.size,
          }],
        }));
      }
      toast.success(tx("تم رفع الملفات", "Files uploaded"));
    } catch (error) {
      toast.error(tx("تعذر رفع الملفات. حاول مرة أخرى.", "File upload failed. Please try again."));
    } finally {
      setUploading(false);
    }
  };

  const removeFile = (id) => set("files", form.files.filter((file) => file.id !== id));

  const submit = async (event) => {
    event.preventDefault();
    if (!form.company_name || !form.full_name || !form.email || !form.whatsapp || !form.country || !form.city || !form.product_description || !form.quantity) {
      toast.error(tx("يرجى استكمال جميع الحقول المطلوبة.", "Please complete all required fields."));
      return;
    }

    setSubmitting(true);
    try {
      const categoryName = selectedService.title.en;
      const description = [
        `European Sourcing Category: ${categoryName}`,
        `Preferred Origin: ${form.preferred_origin || "Europe"}`,
        `Product / Requirement: ${form.product_description}`,
        `Quantity: ${form.quantity}`,
        form.notes ? `Additional Notes: ${form.notes}` : "",
      ].filter(Boolean).join("\n\n");

      const result = await submitRFQ({
        need: "Import from Europe",
        inquiry_type: "European Sourcing & Import",
        material_category: [categoryName],
        material_quantity: form.quantity,
        material_purpose: "Import / Sourcing from Europe",
        project_description: description,
        project_status: "Sourcing Request",
        delivery_time: form.delivery_time,
        priority: "Normal",
        has_documents: form.files.length ? "Yes" : "No",
        document_types: form.files.length ? ["BOQ / Specifications / Product Documents"] : [],
        country: form.country,
        city: form.city,
        project_name: form.project_name || `European Sourcing - ${categoryName}`,
        full_name: form.full_name,
        company_name: form.company_name,
        position: form.position,
        email: form.email,
        mobile: form.mobile,
        whatsapp: form.whatsapp,
        notes: form.notes,
        files: form.files,
      });
      setSuccess(result);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (error) {
      toast.error(tx("تعذر إرسال الطلب. يرجى المحاولة مرة أخرى.", "Submission failed. Please try again."));
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <section className="min-h-[75vh] pt-36 pb-24 px-6 grid place-items-center">
        <div className="max-w-2xl w-full bg-[#11203A] border border-white/10 p-8 sm:p-12 text-center">
          <CheckCircle2 size={58} className="mx-auto text-[#D4AF37] mb-6" />
          <h1 className="font-heading text-3xl sm:text-4xl font-bold text-white mb-4">
            {tx("تم استلام طلب الاستيراد", "Your sourcing request has been received")}
          </h1>
          <p className="text-slate-300 leading-7 mb-6">
            {tx("سيقوم فريق TowerLink بمراجعة المتطلبات والبحث عن خيارات التوريد المناسبة.", "The TowerLink team will review your requirements and identify suitable sourcing options.")}
          </p>
          <div className="bg-[#0A1223] border border-white/10 px-5 py-4 text-sm text-slate-300">
            {tx("رقم المرجع", "Reference")}: <strong className="text-[#D4AF37]">{success.reference}</strong>
          </div>
        </div>
      </section>
    );
  }

  return (
    <div className="bg-[#0A1223] text-white">
      <section className="relative overflow-hidden pt-36 pb-24 px-6 border-b border-white/10">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_15%,rgba(212,175,55,0.12),transparent_34%),radial-gradient(circle_at_80%_70%,rgba(30,64,175,0.12),transparent_36%)]" />
        <div className="relative max-w-6xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.55 }} className="max-w-4xl">
            <div className="inline-flex items-center gap-2 border border-[#D4AF37]/35 bg-[#D4AF37]/8 px-4 py-2 text-xs sm:text-sm text-[#D4AF37] mb-6">
              <Globe2 size={17} />
              {tx("حلول توريد واستيراد للشركات والمشاريع", "European Sourcing for Companies & Projects")}
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight mb-6">
              {tx("الاستيراد من أوروبا", "Import from Europe")}
            </h1>

            <div className="mb-7 inline-flex max-w-3xl items-start gap-4 border border-white/12 bg-white/[0.035] px-5 py-4 sm:px-6 sm:py-5">
              <span
                className="shrink-0 text-4xl sm:text-5xl leading-none drop-shadow-[0_4px_12px_rgba(0,0,0,0.35)]"
                role="img"
                aria-label={tx("علم ألمانيا", "German flag")}
              >
                🇩🇪
              </span>
              <div>
                <h2 className="font-heading text-lg sm:text-xl font-bold text-white mb-2 leading-snug">
                  {tx(
                    "لدينا تواجد في ألمانيا لتلبية طلباتكم من أوروبا",
                    "We have a presence in Germany to support your sourcing needs from Europe"
                  )}
                </h2>
                <p className="text-sm sm:text-base text-slate-300 leading-7">
                  {tx(
                    "حلول توريد احترافية للمشاريع والشركات، مع متابعة أقرب للسوق الأوروبي وخيارات أفضل للموردين والمنتجات.",
                    "Professional sourcing solutions for projects and companies, with closer access to the European market and better supplier and product options."
                  )}
                </p>
              </div>
            </div>

            <p className="text-lg sm:text-xl text-slate-300 leading-8 max-w-3xl mb-8">
              {tx(
                "من البحث عن المورد وحتى تنسيق الشحن والتسليم — TowerLink تساعد الشركات والمشاريع في السعودية ودول الخليج للوصول إلى خيارات توريد أوروبية مناسبة لمتطلباتهم.",
                "From supplier sourcing to logistics coordination, TowerLink helps companies and projects in Saudi Arabia and the GCC access suitable European supply options."
              )}
            </p>
            <a href="#europe-request" className="inline-flex items-center gap-2 bg-[#D4AF37] text-[#0A1223] px-6 py-3.5 font-bold hover:bg-[#B5952F] transition-colors">
              {tx("أرسل طلب استيراد", "Request European Sourcing")} <Arrow size={18} />
            </a>
          </motion.div>
        </div>
      </section>

      <section className="py-24 px-6 bg-[#0D182E]">
        <div className="max-w-6xl mx-auto">
          <div className="max-w-3xl mb-12">
            <p className="text-[#D4AF37] uppercase tracking-[0.18em] text-xs font-bold mb-3">{tx("مجالات التوريد", "SOURCING CATEGORIES")}</p>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-4">{tx("ثلاثة مسارات رئيسية", "Three Core Sourcing Tracks")}</h2>
            <p className="text-slate-400 leading-7">{tx("اختر المسار الأقرب لاحتياج شركتك. يمكن أيضًا إرسال طلب خاص لمنتج أو فئة غير مدرجة.", "Choose the category that best matches your requirement. Custom sourcing requests can also be submitted.")}</p>
          </div>

          <div className="grid lg:grid-cols-3 gap-6">
            {SERVICE_CARDS.map((service) => {
              const Icon = service.icon;
              return (
                <article key={service.id} className="bg-[#11203A] border border-white/10 p-7 hover:border-[#D4AF37]/45 transition-colors">
                  <div className="w-12 h-12 grid place-items-center border border-[#D4AF37]/30 bg-[#D4AF37]/8 text-[#D4AF37] mb-5"><Icon size={24} /></div>
                  <h3 className="font-heading text-xl font-bold mb-3">{service.title[lang]}</h3>
                  <p className="text-sm text-slate-400 leading-6 mb-5">{service.description[lang]}</p>
                  <ul className="space-y-2.5 text-sm text-slate-300">
                    {service.items[lang].map((item) => <li key={item} className="flex items-start gap-2"><span className="text-[#D4AF37] mt-1">•</span><span>{item}</span></li>)}
                  </ul>
                  {service.regulated && (
                    <div className="mt-5 flex items-start gap-2 border-t border-white/10 pt-4 text-xs leading-5 text-amber-200/80">
                      <ShieldCheck size={16} className="shrink-0 mt-0.5" />
                      <span>{tx("التوريد والاستيراد يخضعان للموافقات والتسجيل والمتطلبات التنظيمية المطبقة في بلد الاستيراد.", "Supply and import remain subject to applicable product registration, regulatory and import approvals in the destination country.")}</span>
                    </div>
                  )}
                </article>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-24 px-6 border-y border-white/10">
        <div className="max-w-6xl mx-auto">
          <div className="text-center max-w-3xl mx-auto mb-14">
            <p className="text-[#D4AF37] uppercase tracking-[0.18em] text-xs font-bold mb-3">{tx("آلية العمل", "HOW IT WORKS")}</p>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold">{tx("من الطلب إلى التوريد", "From Request to Supply")}</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {PROCESS.map((step, idx) => {
              const Icon = step.icon;
              return (
                <div key={step.en} className="relative bg-[#11203A] border border-white/10 p-6">
                  <span className="absolute top-4 end-4 text-4xl font-bold text-white/5">0{idx + 1}</span>
                  <Icon size={24} className="text-[#D4AF37] mb-5" />
                  <h3 className="font-bold mb-2">{isAr ? step.ar : step.en}</h3>
                  <p className="text-sm text-slate-400 leading-6">{isAr ? step.arText : step.enText}</p>
                </div>
              );
            })}
          </div>
          <div className="mt-10 grid sm:grid-cols-3 gap-4 text-sm text-slate-300">
            <div className="flex items-center justify-center gap-2 border border-white/10 p-4"><Plane size={18} className="text-[#D4AF37]" />{tx("شحن جوي", "Air Freight")}</div>
            <div className="flex items-center justify-center gap-2 border border-white/10 p-4"><Ship size={18} className="text-[#D4AF37]" />{tx("شحن بحري", "Sea Freight")}</div>
            <div className="flex items-center justify-center gap-2 border border-white/10 p-4"><Truck size={18} className="text-[#D4AF37]" />{tx("شحن بري", "Road Freight")}</div>
          </div>
        </div>
      </section>

      <section id="europe-request" className="py-24 px-6 bg-[#0D182E] scroll-mt-28">
        <div className="max-w-5xl mx-auto">
          <div className="grid lg:grid-cols-[0.8fr_1.2fr] gap-10 items-start">
            <div className="lg:sticky lg:top-28">
              <div className="w-14 h-14 grid place-items-center bg-[#D4AF37] text-[#0A1223] mb-6"><PackageSearch size={28} /></div>
              <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-4">{tx("طلب استيراد من أوروبا", "Request European Sourcing")}</h2>
              <p className="text-slate-400 leading-7 mb-6">{tx("أرسل تفاصيل المنتج أو الـ BOQ أو المواصفات. سيظهر الطلب مباشرة ضمن طلبات عروض الأسعار في منصة TowerLink.", "Send the product details, BOQ or specifications. Your request will be submitted directly to the TowerLink RFQ workflow.")}</p>

              <a
                href="mailto:imports@towerlinkgcc.com"
                className="mb-6 flex items-center gap-3 border border-white/10 bg-[#11203A] px-5 py-4 text-sm text-slate-300 hover:border-[#D4AF37]/45 hover:text-white transition-colors"
                aria-label={tx("مراسلة قسم الاستيراد عبر البريد الإلكتروني", "Email the imports team")}
              >
                <span className="w-10 h-10 shrink-0 grid place-items-center bg-[#D4AF37] text-[#0A1223]">
                  <Mail size={19} />
                </span>
                <span>
                  <span className="block text-xs text-slate-500 mb-1">{tx("للاستفسارات المباشرة", "Direct import enquiries")}</span>
                  <strong className="text-[#D4AF37] font-semibold">imports@towerlinkgcc.com</strong>
                </span>
              </a>

              <div className="border border-[#D4AF37]/25 bg-[#D4AF37]/5 p-5 text-sm text-slate-300 leading-6">
                <ShieldCheck className="text-[#D4AF37] mb-3" size={22} />
                {tx("لا تمثل هذه الخدمة ضمانًا لتوفر المنتج أو الحصول على الموافقات التنظيمية. يتم تأكيد التوريد بعد مراجعة المورد والمتطلبات النظامية واللوجستية.", "This service does not guarantee product availability or regulatory approval. Supply is confirmed only after supplier, regulatory and logistics review.")}
              </div>
            </div>

            <form onSubmit={submit} className="bg-[#11203A] border border-white/10 p-6 sm:p-8 space-y-6">
              <Field label={tx("فئة الطلب", "Sourcing Category")} required>
                <select value={form.category} onChange={(e) => set("category", e.target.value)} className={inputClass}>
                  {SERVICE_CARDS.map((item) => <option key={item.id} value={item.id}>{item.title[lang]}</option>)}
                </select>
              </Field>

              <Field label={tx("وصف المنتج أو المتطلبات", "Product / Requirement Description")} required>
                <textarea value={form.product_description} onChange={(e) => set("product_description", e.target.value)} rows={5} className={inputClass} placeholder={tx("اكتب المواصفات، الموديل، العلامة المطلوبة أو أي تفاصيل فنية...", "Describe specifications, model, preferred brand or technical requirements...")} />
              </Field>

              <div className="grid sm:grid-cols-2 gap-5">
                <Field label={tx("الكمية المطلوبة", "Required Quantity")} required>
                  <input value={form.quantity} onChange={(e) => set("quantity", e.target.value)} className={inputClass} placeholder={tx("مثال: 500 قطعة", "e.g. 500 units")} />
                </Field>
                <Field label={tx("بلد المنشأ المفضل", "Preferred Origin")}>
                  <input value={form.preferred_origin} onChange={(e) => set("preferred_origin", e.target.value)} className={inputClass} placeholder={tx("ألمانيا، إيطاليا، أوروبا...", "Germany, Italy, Europe...")} />
                </Field>
                <Field label={tx("موعد التسليم المطلوب", "Required Delivery")}>
                  <input value={form.delivery_time} onChange={(e) => set("delivery_time", e.target.value)} className={inputClass} placeholder={tx("مثال: خلال 6 أسابيع", "e.g. Within 6 weeks")} />
                </Field>
                <Field label={tx("اسم المشروع / المرجع", "Project / Reference Name")}>
                  <input value={form.project_name} onChange={(e) => set("project_name", e.target.value)} className={inputClass} />
                </Field>
              </div>

              <div className="border-t border-white/10 pt-6">
                <h3 className="font-heading text-lg font-bold mb-5">{tx("بيانات الشركة", "Company Details")}</h3>
                <div className="grid sm:grid-cols-2 gap-5">
                  <Field label={tx("اسم الشركة", "Company Name")} required><input value={form.company_name} onChange={(e) => set("company_name", e.target.value)} className={inputClass} /></Field>
                  <Field label={tx("اسم المسؤول", "Contact Person")} required><input value={form.full_name} onChange={(e) => set("full_name", e.target.value)} className={inputClass} /></Field>
                  <Field label={tx("المنصب", "Position")}><input value={form.position} onChange={(e) => set("position", e.target.value)} className={inputClass} /></Field>
                  <Field label={tx("البريد الإلكتروني", "Email")} required><input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className={inputClass} /></Field>
                  <Field label={tx("الهاتف", "Phone")}><input value={form.mobile} onChange={(e) => set("mobile", e.target.value)} className={inputClass} placeholder="+966..." /></Field>
                  <Field label={tx("واتساب", "WhatsApp")} required><input value={form.whatsapp} onChange={(e) => set("whatsapp", e.target.value)} className={inputClass} placeholder="+966..." /></Field>
                  <Field label={tx("الدولة", "Country")} required><input value={form.country} onChange={(e) => set("country", e.target.value)} className={inputClass} /></Field>
                  <Field label={tx("المدينة", "City")} required><input value={form.city} onChange={(e) => set("city", e.target.value)} className={inputClass} /></Field>
                </div>
              </div>

              <Field label={tx("ملاحظات إضافية", "Additional Notes")}>
                <textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} rows={3} className={inputClass} />
              </Field>

              <div>
                <span className="block mb-2 text-sm font-medium text-slate-300">{tx("إرفاق BOQ / مواصفات / صور", "Attach BOQ / Specifications / Images")}</span>
                <input ref={fileRef} type="file" multiple className="hidden" onChange={(e) => handleFiles(e.target.files)} />
                <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} className="w-full border border-dashed border-white/20 bg-[#0A1223] hover:border-[#D4AF37]/60 transition-colors px-5 py-6 text-slate-300 inline-flex items-center justify-center gap-2">
                  {uploading ? <Loader2 className="animate-spin" size={19} /> : <Upload size={19} />}
                  {uploading ? tx("جاري الرفع...", "Uploading...") : tx("اختيار الملفات", "Choose Files")}
                </button>
                {form.files.length > 0 && <div className="mt-3 space-y-2">{form.files.map((file) => <div key={file.id} className="flex items-center justify-between gap-3 bg-[#0A1223] border border-white/10 px-3 py-2 text-xs text-slate-300"><span className="truncate">{file.original_filename}</span><button type="button" onClick={() => removeFile(file.id)} className="text-slate-500 hover:text-red-400"><X size={16} /></button></div>)}</div>}
              </div>

              <button type="submit" disabled={submitting || uploading} className="w-full bg-[#D4AF37] text-[#0A1223] px-6 py-4 font-bold inline-flex items-center justify-center gap-2 hover:bg-[#B5952F] disabled:opacity-60 disabled:cursor-not-allowed transition-colors">
                {submitting ? <Loader2 className="animate-spin" size={20} /> : <Arrow size={20} />}
                {submitting ? tx("جاري إرسال الطلب...", "Submitting...") : tx("إرسال طلب الاستيراد", "Submit Sourcing Request")}
              </button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
}
